// src/features/learner/LearnerDashboard.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    getDoc,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import logo from '../../assets/logo_1000.png';
import './LearnerDashboard.css';
import MonthlySurveyCard from '../surveys/MonthlySurveyCard';

// ---------------------------------------------------------------------------
// LearnerDashboard
// ---------------------------------------------------------------------------
const LearnerDashboard = React.memo(() => {
    const { currentUser, logout } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [catalogs, setCatalogs] = useState([]);
    const [error, setError] = useState(null);
    const [userName, setUserName] = useState(null);
    const [cohortId, setCohortId] = useState(null); // saved with survey answers

    // Groups for the profile popover
    const [learnerGroups, setLearnerGroups] = useState([]);
    const [profileOpen, setProfileOpen] = useState(false);
    const profileRef = useRef(null);

    const userId = useMemo(() => currentUser?.uid, [currentUser?.uid]);

    // Close popover on outside click
    useEffect(() => {
        if (!profileOpen) return;
        const handleClick = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) {
                setProfileOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [profileOpen]);

    // Fetch groups this learner belongs to (runs once after userId is known)
    const fetchLearnerGroups = useCallback(async (uid) => {
        try {
            const snap = await getDocs(query(
                collection(db, 'tutor_groups'),
                where('learner_ids', 'array-contains', uid)
            ));
            const groups = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            // Sort by name so Wednesday comes before Friday etc.
            groups.sort((a, b) => a.name.localeCompare(b.name));
            setLearnerGroups(groups);
        } catch (err) {
            console.error('Error fetching learner groups:', err);
        }
    }, []);

    const fetchDashboardData = useCallback(async (uid) => {
        try {
            setLoading(true);
            setError(null);

            const [userDoc, userCatalogsSnapshot, progressSnap] = await Promise.all([
                getDoc(doc(db, 'users', uid)),
                getDocs(query(collection(db, 'userCatalogs'), where('userId', '==', uid))),
                getDoc(doc(db, 'learnerProgress', uid))
            ]);

            setUserName(userDoc.exists() ? userDoc.data().name : null);
            setCohortId(userDoc.exists() ? (userDoc.data().cohortId || null) : null);

            const catalogIds = userCatalogsSnapshot.docs.map(d => d.data().catalogId);

            if (catalogIds.length === 0) {
                setCatalogs([]);
                setLoading(false);
                return;
            }

            const progressData = progressSnap.exists() ? progressSnap.data() : {};
            const lastVisitedLesson = progressData.lastVisitedLesson || null;

            const catalogDocs = await Promise.all(
                catalogIds.map(id => getDoc(doc(db, 'catalogs', id)))
            );

            const lessonsPerCatalog = await Promise.all(
                catalogIds.map(catalogId =>
                    getDocs(query(
                        collection(db, 'lessons'),
                        where('catalogId', '==', catalogId)
                    )).then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })))
                )
            );

            const enhancedCatalogs = catalogDocs
                .map((d, i) => {
                    if (!d.exists()) return null;
                    const catalog = { id: catalogIds[i], ...d.data() };
                    const catalogLessons = lessonsPerCatalog[i];
                    const totalLessons = catalogLessons.length;

                    const visitedLessonIds = new Set(progressData.visitedLessonIds || []);
                    const visitedLessons = catalogLessons.filter(l =>
                        visitedLessonIds.has(l.id)
                    ).length;

                    // completedLessons: lessons where at least one nav item is fully teal.
                    // Uses completedNavItems map — { navItemId: { lessonId, completedAt } }
                    const rawCompletedNavItems = progressData.completedNavItems || {};
                    const completedLessonIds = new Set(
                        Object.values(rawCompletedNavItems)
                            .map(v => v.lessonId)
                            .filter(Boolean)
                    );
                    const completedLessons = catalogLessons.filter(l =>
                        completedLessonIds.has(l.id)
                    ).length;

                    let lastAccessedLesson = null;
                    if (lastVisitedLesson && catalogLessons.some(l => l.id === lastVisitedLesson.lessonId)) {
                        const lessonDoc = catalogLessons.find(l => l.id === lastVisitedLesson.lessonId);
                        const ts = lastVisitedLesson.timestamp?.toDate
                            ? lastVisitedLesson.timestamp.toDate()
                            : new Date(lastVisitedLesson.timestamp);
                        lastAccessedLesson = {
                            id: lastVisitedLesson.lessonId,
                            title: lessonDoc?.title || '',
                            lastAccessTime: ts
                        };
                    }

                    return { ...catalog, totalLessons, visitedLessons, completedLessons, lastAccessedLesson };
                })
                .filter(Boolean);

            setCatalogs(enhancedCatalogs);

        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('Failed to load your dashboard. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!userId) { setLoading(false); return; }
        fetchDashboardData(userId);
        fetchLearnerGroups(userId);
    }, [userId, fetchDashboardData, fetchLearnerGroups]);

    const recentActivity = useMemo(() => {
        let mostRecent = null;
        let mostRecentTime = 0;
        for (const catalog of catalogs) {
            const la = catalog.lastAccessedLesson;
            if (la && la.lastAccessTime && la.lastAccessTime.getTime() > mostRecentTime) {
                mostRecentTime = la.lastAccessTime.getTime();
                mostRecent = {
                    lessonId: la.id,
                    lessonTitle: la.title,
                    catalogId: catalog.id,
                    catalogTitle: catalog.title,
                    timestamp: la.lastAccessTime
                };
            }
        }
        return mostRecent;
    }, [catalogs]);

    const navigateToCatalog = useCallback((catalog) => {
        navigate(`/learn/catalog/${catalog.id}`);
    }, [navigate]);

    const navigateToLesson = useCallback((lessonId) => {
        navigate(`/learn/lesson/${lessonId}`);
    }, [navigate]);

    const handleLogout = useCallback(async () => {
        try { await logout(); } catch (err) { console.error('Logout failed:', err); }
    }, [logout]);

    const handleContinueLearning = useCallback((catalog) => {
        if (catalog.lastAccessedLesson) {
            navigateToLesson(catalog.lastAccessedLesson.id);
        } else {
            navigateToCatalog(catalog);
        }
    }, [navigateToCatalog, navigateToLesson]);

    const handleRefresh = useCallback(() => {
        if (userId) fetchDashboardData(userId);
    }, [userId, fetchDashboardData]);

    // Initials for the avatar button
    const initials = useMemo(() => {
        const name = userName || currentUser?.email || '';
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return name.slice(0, 2).toUpperCase();
    }, [userName, currentUser]);

    // ── Render states ──────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="dashboard-loading">
                <img src={logo} alt="Litvill Learn" className="loading-logo" />
                <p>Loading your dashboard…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-error">
                <p>{error}</p>
                <button onClick={handleRefresh} className="retry-button">Try Again</button>
            </div>
        );
    }

    if (!currentUser) {
        return (
            <div className="dashboard-error">
                Please log in to access your learning dashboard.
            </div>
        );
    }

    return (
        <div className="learner-dashboard">

            {/* Top navigation bar */}
            <header className="dashboard-navbar">
                <div className="navbar-brand">
                    <img src={logo} alt="Litvill Learn logo" className="navbar-logo" />
                    <span className="navbar-title">Litvill Learn</span>
                </div>
                <div className="navbar-actions">

                    {/* Profile pill + popover */}
                    <div className="profile-popover-anchor" ref={profileRef}>
                        <button
                            className={`profile-pill ${profileOpen ? 'open' : ''}`}
                            onClick={() => setProfileOpen(v => !v)}
                            aria-label="My profile and groups"
                        >
                            <span className="profile-avatar">{initials}</span>
                            <span className="profile-name">
                                {userName || currentUser?.email || 'Learner'}
                            </span>
                            <span className="profile-chevron">{profileOpen ? '▲' : '▼'}</span>
                        </button>

                        {profileOpen && (
                            <div className="profile-popover">
                                {/* Header */}
                                <div className="popover-header">
                                    <span className="popover-avatar">{initials}</span>
                                    <div>
                                        <div className="popover-name">
                                            {userName || currentUser?.email}
                                        </div>
                                        <div className="popover-email">{currentUser?.email}</div>
                                    </div>
                                </div>

                                {/* Groups section */}
                                <div className="popover-section-label">My tutoring groups</div>
                                {learnerGroups.length === 0 ? (
                                    <p className="popover-no-groups">No groups assigned yet.</p>
                                ) : (
                                    <ul className="popover-groups-list">
                                        {learnerGroups.map(group => (
                                            <li key={group.id} className="popover-group-row">
                                                <div className="popover-group-info">
                                                    {/* Calendar icon */}
                                                    <svg className="popover-group-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <rect x="3" y="4" width="18" height="18" rx="2" stroke="#6b7280" strokeWidth="2" fill="none"/>
                                                        <path d="M3 9h18" stroke="#6b7280" strokeWidth="2"/>
                                                        <path d="M8 2v4M16 2v4" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
                                                    </svg>
                                                    <span className="popover-group-name">{group.name}</span>
                                                </div>
                                                {group.meetLink ? (
                                                    <a
                                                        href={group.meetLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="popover-meet-btn"
                                                        onClick={() => setProfileOpen(false)}
                                                    >
                                                        {/* Meet camera icon */}
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M15 8.5V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-2.5l4 3v-13l-4 3Z" fill="white"/>
                                                        </svg>
                                                        Join Meet
                                                    </a>
                                                ) : (
                                                    <span className="popover-no-meet">No link yet</span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                <div className="popover-divider" />

                                {/* Logout */}
                                <button className="popover-logout-btn" onClick={handleLogout}>
                                    Log out
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* Welcome strip */}
            <div className="welcome-strip">
                <p className="welcome-message">
                    Welcome back, <strong>{userName || currentUser?.email || 'Learner'}</strong>!
                </p>
            </div>

            {/* Monthly feedback invitation — hides itself when not due */}
            <MonthlySurveyCard
                userId={userId}
                userName={userName || currentUser?.email}
                userEmail={currentUser?.email}
                cohortId={cohortId}
                groups={learnerGroups}
            />

            {/* Continue Learning banner */}
            {recentActivity && (
                <div className="recent-activity">
                    <h2 className="section-title">Continue where you left off</h2>
                    <div
                        className="activity-card"
                        onClick={() => navigateToLesson(recentActivity.lessonId)}
                    >
                        <div className="activity-icon">📚</div>
                        <div className="activity-details">
                            <h3>{recentActivity.lessonTitle}</h3>
                            <p className="activity-catalog">{recentActivity.catalogTitle}</p>
                            <p className="activity-time">
                                Last visited: {recentActivity.timestamp.toLocaleDateString()}
                            </p>
                        </div>
                        <button className="continue-button">Resume →</button>
                    </div>
                </div>
            )}

            {/* Catalog cards */}
            <div className="catalogs-section">
                {catalogs.length === 0 ? (
                    <div className="no-catalogs">
                        <div className="empty-state-icon">📚</div>
                        <h3>No Courses Assigned</h3>
                        <p>You do not have any courses assigned yet. Please contact your administrator.</p>
                    </div>
                ) : (
                    <>
                        <h2 className="section-title">My Courses ({catalogs.length})</h2>
                        <div className="catalogs-grid">
                            {catalogs.map(catalog => {
                                const progressPercentage = catalog.totalLessons > 0
                                    ? Math.round((catalog.completedLessons / catalog.totalLessons) * 100)
                                    : 0;
                                return (
                                    <CatalogCard
                                        key={catalog.id}
                                        catalog={catalog}
                                        progressPercentage={progressPercentage}
                                        onContinueLearning={handleContinueLearning}
                                        onViewCatalog={navigateToCatalog}
                                    />
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
});

// ---------------------------------------------------------------------------
// CatalogCard
// ---------------------------------------------------------------------------
const CatalogCard = React.memo(({ catalog, progressPercentage, onContinueLearning, onViewCatalog }) => {
    const isComplete = catalog.totalLessons > 0 && catalog.completedLessons >= catalog.totalLessons;

    return (
        <div className="catalog-card">
            <div className="catalog-header">
                <h3 className="catalog-title">{catalog.title}</h3>
                <div className="progress-circle" aria-label={`${progressPercentage}% visited`}>
                    <svg viewBox="0 0 36 36" className="progress-ring">
                        <path
                            className="progress-ring-bg"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                        <path
                            className="progress-ring-fill"
                            strokeDasharray={`${progressPercentage}, 100`}
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                    </svg>
                    <span className="progress-percentage">{progressPercentage}%</span>
                </div>
            </div>

            <p className="catalog-description">{catalog.description}</p>

            <div className="progress-container">
                <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${progressPercentage}%` }} />
                </div>
                {isComplete ? (
                    <div className="progress-text" style={{ color: '#ff4747', fontWeight: 600 }}>
                        🎉 All lessons visited — ready to test?
                    </div>
                ) : (
                    <div className="progress-text">
                        {catalog.completedLessons}/{catalog.totalLessons} lessons completed
                    </div>
                )}
            </div>

            <div className="catalog-actions">
                {isComplete ? (
                    <>
                        <a
                            href="https://tests.litvill.com"
                            className="primary-button"
                            style={{ textAlign: 'center', textDecoration: 'none', display: 'block' }}
                        >
                            Go to Tests →
                        </a>
                        <button className="secondary-button" onClick={() => onViewCatalog(catalog)}>
                            View All Lessons
                        </button>
                    </>
                ) : (
                    <>
                        <button className="primary-button" onClick={() => onContinueLearning(catalog)}>
                            {catalog.lastAccessedLesson ? 'Continue Learning' : 'Start Learning'}
                        </button>
                        <button className="secondary-button" onClick={() => onViewCatalog(catalog)}>
                            View All Lessons
                        </button>
                    </>
                )}
            </div>
        </div>
    );
});

CatalogCard.displayName = 'CatalogCard';
LearnerDashboard.displayName = 'LearnerDashboard';

export default LearnerDashboard;